import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'

import { dropResource } from '../../../shared/model/keys'
import type { PricesIntentState, PricesRidIntent } from './types'

const initialState: PricesIntentState = { byResource: {} }
const EMPTY: PricesRidIntent = { page: 0, search: '', query: '' }
const MAX_SEARCH = 128

/** Ключ rid — в нижнем регистре и с «|»: сброс области ресурса убирает ровно его запись. */
export const pricesKey = (resourceId: string) => `${resourceId.toLowerCase()}|prices`

function intentOf(state: PricesIntentState, resourceId: string): PricesRidIntent {
  const key = pricesKey(resourceId)
  state.byResource[key] ??= { ...EMPTY }
  return state.byResource[key]
}

/** Намерение окна цен по rid. Срок — `screen`: снимок перечитывается, поиск — дело одного захода. */
const pricesSlice = createSlice({
  name: 'aqsi/prices',
  initialState,
  reducers: {
    pricesSearchChanged(state, action: PayloadAction<{ resourceId: string; text: string }>) {
      intentOf(state, action.payload.resourceId).search = action.payload.text.slice(0, MAX_SEARCH)
    },
    /** Поиск уходит в службу целиком: новая выдача — с первой страницы. */
    pricesSearchSubmitted(state, action: PayloadAction<{ resourceId: string; text?: string }>) {
      const intent = intentOf(state, action.payload.resourceId)
      if (action.payload.text !== undefined) intent.search = action.payload.text.slice(0, MAX_SEARCH)
      intent.query = intent.search.trim()
      intent.page = 0
    },
    pricesPageChanged(state, action: PayloadAction<{ resourceId: string; page: number }>) {
      intentOf(state, action.payload.resourceId).page = Math.max(0, Math.trunc(action.payload.page))
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => dropResource(state.byResource, action.payload.resourceId))
  },
})

export const { pricesSearchChanged, pricesSearchSubmitted, pricesPageChanged } = pricesSlice.actions
export const pricesReducer = pricesSlice.reducer

export function pricesIntentOf(state: PricesIntentState, resourceId: string): PricesRidIntent {
  return state.byResource[pricesKey(resourceId)] ?? EMPTY
}
